import { Typography } from "@mui/joy";
import { Box } from "@mui/material";
import { NerdDateTimeRangePicker } from "@nerdjs/nerd-ui";
import { DateTime } from "luxon";
import moment from "moment-timezone";

/**
 *
 * @param root0
 * @param root0.dateFrom
 * @param root0.dateTo
 * @param root0.tzName
 * @param root0.locked
 * @param root0.onChange
 * @returns {ReactElement} TestDatePicker page
 */
export function DateTimePicker({
  dateFrom,
  dateTo,
  tzName,
  locked,
  onChange,
}: {
  dateFrom: string;
  dateTo: string;
  tzName: string;
  locked?: boolean;
  onChange: (dateFrom: string, dateTo: string) => void;
}) {
  // tzName is the effective zone resolved by the caller (venue → gig → browser
  // guess); the guess here is only a defensive fallback.
  const _tzName = tzName ?? moment.tz.guess();
  // Human-readable abbreviation (e.g. PST/EDT) anchored to the entered date so
  // the user always sees which zone they are typing in.
  const anchor = dateFrom ?? moment().toISOString();
  const tzAbbr = moment.tz(anchor, _tzName).format("z");
  return (
    <Box>
      <NerdDateTimeRangePicker
        key={_tzName}
        // defaultTimeZone={"UTC"}
        disableAllDay
        disableTimeZones
        timeFormat="12"
        startDate={
          dateFrom ? utcToLocal(DateTime.fromISO(dateFrom), _tzName) : null
        }
        endDate={dateTo ? utcToLocal(DateTime.fromISO(dateTo), _tzName) : null}
        onChange={(startDate, endDate) => {
          onChange(
            localToUTC(startDate, _tzName).toISO(),
            localToUTC(endDate, _tzName).toISO()
          );
        }}
      />
      <Typography
        level="body3"
        startDecorator={<i className="fa-solid fa-earth-americas"></i>}
        sx={{ mt: 0.5 }}
      >
        Times shown in {_tzName} ({tzAbbr})
        {locked ? " — locked to the venue" : ""}
      </Typography>
    </Box>
  );
}

function localToUTC(date: DateTime, tzName: string): DateTime {
  return date.setZone(tzName, { keepLocalTime: true }).toUTC();
}

function utcToLocal(date: DateTime, tzName: string): DateTime {
  return date.setZone(tzName).setZone("UTC", { keepLocalTime: true });
}
